import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * MBS Hukuk kurumsal defter kabuğu — sayfa çevirme animasyonu
 */
export default function CalendarDefter({
    children,
    monthLabel,
    pageNumber,
    onPrevPage,
    onNextPage,
    pageTurn,
    sidebar,
}) {
    return (
        <div className="defter-desk relative min-h-[70vh] py-6 px-2 md:px-8">
            <style>{`
                .defter-desk {
                    background: radial-gradient(ellipse at 50% 0%, #3d2817 0%, #1a0f08 55%, #0d0806 100%);
                }
                .defter-book {
                    perspective: 2200px;
                    max-width: 1400px;
                    margin: 0 auto;
                }
                .defter-spine {
                    background: linear-gradient(90deg, #6b4f2a 0%, #c9a961 35%, #8b6914 50%, #c9a961 65%, #5c4020 100%);
                    box-shadow: inset 0 0 12px rgba(0,0,0,0.4);
                }
                .defter-page {
                    background: linear-gradient(135deg, #faf6ee 0%, #f3ebe0 40%, #faf6ee 100%);
                    box-shadow: 4px 8px 32px rgba(0,0,0,0.45), inset 0 0 80px rgba(201,169,97,0.06);
                    transform-origin: left center;
                    transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s;
                }
                .defter-page.turn-next {
                    transform: rotateY(-8deg) scale(0.985);
                    opacity: 0.92;
                }
                .defter-page.turn-prev {
                    transform: rotateY(8deg) scale(0.985);
                    opacity: 0.92;
                }
                .defter-ruled {
                    background-image: repeating-linear-gradient(
                        transparent,
                        transparent 27px,
                        rgba(201,169,97,0.12) 27px,
                        rgba(201,169,97,0.12) 28px
                    );
                }
                .defter-watermark {
                    opacity: 0.045;
                    pointer-events: none;
                }
            `}</style>

            <div className="defter-book flex flex-col xl:flex-row gap-0 rounded-sm overflow-hidden shadow-2xl">
                {/* Sol cilt / spine */}
                <div className="defter-spine hidden xl:flex w-5 shrink-0 flex-col items-center justify-center py-8">
                    <div
                        className="text-[#2a1810] font-display text-[10px] tracking-[0.3em] uppercase"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    >
                        MBS Hukuk
                    </div>
                </div>

                {/* Ana sayfa */}
                <div
                    className={`defter-page flex-1 min-w-0 relative ${pageTurn ? `turn-${pageTurn}` : ""}`}
                >
                    {/* Üst başlık şeridi */}
                    <div className="relative border-b border-[#c9a961]/30 bg-gradient-to-r from-[#f5ecd6] via-[#faf6ee] to-[#f5ecd6] px-4 md:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <img
                                src="/static/brand/mbs-logo.png"
                                alt="MBS Hukuk"
                                className="h-10 w-10 object-contain"
                                onError={(e) => {
                                    e.currentTarget.src = "/static/img/mbs-logo.png";
                                }}
                            />
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8b6914] font-bold">
                                    MBS Hukuk & Danışmanlık
                                </p>
                                <h2 className="font-display text-lg md:text-xl text-[#1a2744] capitalize">
                                    Ajanda Defteri · {monthLabel}
                                </h2>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={onPrevPage}
                                className="h-9 w-9 rounded-full border border-[#c9a961]/50 bg-white/60 text-[#1a2744] hover:bg-[#c9a961]/20 flex items-center justify-center shadow-sm"
                                title="Önceki sayfa"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-xs font-mono text-[#8b7355] min-w-[60px] text-center">
                                Sayfa {pageNumber}
                            </span>
                            <button
                                type="button"
                                onClick={onNextPage}
                                className="h-9 w-9 rounded-full border border-[#c9a961]/50 bg-white/60 text-[#1a2744] hover:bg-[#c9a961]/20 flex items-center justify-center shadow-sm"
                                title="Sonraki sayfa"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Filigran logo */}
                    <img
                        src="/static/brand/mbs-logo.png"
                        alt=""
                        aria-hidden
                        className="defter-watermark absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 object-contain"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />

                    <div className="defter-ruled relative px-3 md:px-5 py-4 text-[#1a2744]">
                        {children}
                    </div>

                    <div className="border-t border-[#c9a961]/25 px-6 py-2 flex justify-between text-[9px] text-[#8b7355] uppercase tracking-wider">
                        <span>0505 115 34 79 · mbshukuk.com</span>
                        <span>Sağ tık → ekle / düzenle</span>
                    </div>
                </div>

                {/* Sağ yapışkan not paneli (defter kenarı) */}
                {sidebar && (
                    <div className="xl:w-72 shrink-0 bg-[#2a1810]/90 border-l border-[#c9a961]/20 p-3 space-y-3">
                        {sidebar}
                    </div>
                )}
            </div>
        </div>
    );
}
